import React, { useState } from "react";
import {
  Modal,
  Box,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  makeStyles,
  IconButton,
} from "@material-ui/core";
import { Minus, Plus, X } from "lucide-react";
import ProgressSteps from "./progressSteps";
import CCBillPayment from "./CCBillPayment";
import { showToast } from "../../utils/toast";

const useStyles = makeStyles(theme => ({
  modalSubscriptionWrapper: {
    // Wrapper for backdrop
  },
  modalTipWrapper: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#131313",
    padding: "20px",
    width: "560px",
    borderRadius: "12px",
    zIndex: 1401,
    boxShadow: "0px 4px 20px rgba(0,0,0,0.5)",
    [theme.breakpoints.down("sm")]: {
      width: "90%",
      padding: "0",
    },
  },
  modalHeader: {
    borderRadius: "12px 12px 0 0",
    backgroundColor: "#131313",
    padding: "20px 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      padding: "32px 16px 0"
    },
  },
  modalTitle: {
    color: "#FFFFFF",
    fontSize: "28px",
    fontWeight: 400,
    margin: 0,
    padding: 0,
    [theme.breakpoints.down("sm")]: {
      fontSize: "20px",
    },
  },
  closeButton: {
    color: "#FFFFFF",
    padding: "4px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  modalContent: {
    backgroundColor: "#131313",
    color: "#fff",
    padding: "24px",
    [theme.breakpoints.down("sm")]: {
      padding: "24px 16px",
    },
  },
  selectTipLabel: {
    color: "#FFFFFF",
    fontSize: "20px",
    fontWeight: 700,
    marginBottom: "16px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
  selectTipDivider: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "16px auto",
    position: "relative",
    width: "100%",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      top: "50%",
      height: "1px",
      backgroundColor: "#1F1F1F",
    },
  },
  selectTipDividerText: {
    position: "relative",
    backgroundColor: "#131313",
    padding: "0 16px",
    color: "#FFFFFFCC",
    fontSize: "16px",
  },
  suggestedTipsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "8px",
    marginBottom: "24px",
    flexWrap: "wrap",
  },
  suggestedTipButton: {
    backgroundColor: "transparent",
    border: "1px solid #747474",
    color: "#FFFFFF",
    fontSize: "16px",
    fontWeight: 700,
    padding: "12px 24px",
    borderRadius: "8px",
    minWidth: "80px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      border: "1px solid #747474",
    },
    "&.selected": {
      border: "1px solid #FE5A97",
      backgroundColor: "rgba(254, 90, 151, 0.1)",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "4px 0",
      minWidth: "56px",
    },
  },
  enterAmountContainer: {
    marginTop: "24px",
  },
  enterAmountHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  enterAmountLabel: {
    color: "#FFFFFF",
    fontSize: "20px",
    fontWeight: 700,
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
  minimumTipText: {
    color: "#FFFFFFCC",
    fontSize: "14px",
    fontWeight: 400,
  },
  tipInputContainer: {
    position: "relative",
    width: "100%",
  },
  tipInfoText: {
    marginTop: "20px",
    color: "#FFFFFF",
    fontSize: "16px",
    textAlign: "left",
    lineHeight: "1.5",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
    },
  },
  tipInput: {
    "& input": {
      color: "#fff",
      backgroundColor: "transparent",
      fontSize: "16px",
      fontWeight: 700,
      textAlign: "center",
      padding: "0 60px",
      height: "60px",
      MozAppearance: "textfield",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      border: "1px solid #363636",
      "&:hover": {
        border: "1px solid #747474",
      },
      "&.Mui-focused": {
        border: "1px solid #FE5A97",
      },
    },
    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
  },
  decrementButton: {
    position: "absolute",
    left: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#FFFFFF",
    borderRadius: "6px",
    backgroundColor: "#1F1F1F",
    width: "44px",
    height: "44px",
    minWidth: "32px",
    padding: 0,
    zIndex: 1,
    "&:hover": {
      backgroundColor: "#474747",
    },
    "&:disabled": {
      backgroundColor: "#1F1F1F",
      color: "#666",
    },
  },
  incrementButton: {
    position: "absolute",
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#FFFFFF",
    borderRadius: "6px",
    backgroundColor: "#1F1F1F",
    width: "44px",
    height: "44px",
    minWidth: "32px",
    padding: 0,
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: "#474747",
    },
    "&:disabled": {
      backgroundColor: "#1F1F1F",
      color: "#666",
    },
  },
  modalActions: {
    borderRadius: "0 0 12px 12px",
    backgroundColor: "#131313",
    padding: "16px 24px",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      padding: "0 16px 32px",
    },
  },
  cancelButton: {
    color: "#fff",
    padding: "12px 40px",
    maxWidth: "142px",
    fontSize: "16px",
    borderRadius: "6px",
    border: "1px solid #FE5A97",
    textTransform: "capitalize",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "#FE5A97",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100px",
      fontSize: "14px",
    },
  },
  sendButton: {
    color: "#fff",
    padding: "12px 40px",
    maxWidth: "240px",
    fontSize: "16px",
    borderRadius: "6px",
    textTransform: "capitalize",
    backgroundColor: "#FE5A97",
    textWrap: "nowrap",
    "&:hover": {
      border: "1px solid #FE5A97",
      backgroundColor: "transparent",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "142px",
      fontSize: "14px",
    },
  },
}));

export interface TipDialogProps {
  open: boolean;
  onClose: () => void;
  suggestedTips?: number[];
  minimumTipAmount?: number;
  username?: string;
  creatorName?: string;
  tipForUserId?: string;
  userId?: string;
  email?: string;
  conversationId?: string;
  onPaymentSuccess?: (amount: number) => void;
  validateBeforeStep1?: (amount: number) => boolean;
}

const TipDialog: React.FC<TipDialogProps> = ({
  open,
  onClose,
  suggestedTips = [],
  minimumTipAmount,
  username,
  creatorName,
  tipForUserId,
  userId,
  email,
  conversationId,
  onPaymentSuccess,
  validateBeforeStep1,
}) => {
  const classes = useStyles();
  const [tipAmount, setTipAmount] = useState<number>(0);
  const [tipStep, setTipStep] = useState(0);
  const [selectedSuggestedAmount, setSelectedSuggestedAmount] = useState<
    number | null
  >(null);

  const handleClose = () => {
    setTipAmount(0);
    setTipStep(0);
    setSelectedSuggestedAmount(null);
    onClose();
  };

  const handleSuggestedTipClick = (amount: number) => {
    setTipAmount(amount);
    setSelectedSuggestedAmount(amount);
  };

  const handleIncrement = () => {
    const newAmount = (tipAmount || 0) + 1;
    setTipAmount(newAmount);
    setSelectedSuggestedAmount(null);
  };

  const handleDecrement = () => {
    const newAmount = Math.max(0, (tipAmount || 0) - 1);
    setTipAmount(newAmount);
    setSelectedSuggestedAmount(null);
  };

  const handleTip = async () => {
    if (tipStep === 0) {
      if (!tipAmount || tipAmount <= 0) {
        showToast("Please enter a valid tip amount.", "error");
        return;
      }

      if (minimumTipAmount && tipAmount < minimumTipAmount) {
        showToast(`Minimum tip amount is $${minimumTipAmount}`, "error");
        return;
      }

      // Custom validation if provided
      if (validateBeforeStep1 && !validateBeforeStep1(tipAmount)) {
        return;
      }

      setTipStep(1);
      return;
    }
  };

  const handlePaymentSuccess = () => {
    if (onPaymentSuccess && tipAmount > 0) {
      onPaymentSuccess(tipAmount);
    }
    // Show success message
    showToast(
      `Successfully tipped $${tipAmount} to ${username || creatorName || "this creator"}!`,
      "success"
    );
    // Allow user to close the window
    handleClose();
  };

  return (
    <Modal
      open={open}
      disablePortal
      disableEnforceFocus
      disableScrollLock
      BackdropProps={{
        onClick: handleClose,
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
      className={classes.modalSubscriptionWrapper}
    >
      <Box
        className={classes.modalTipWrapper}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
        }}
      >
        <Box className={classes.modalHeader}>
          <Typography className={classes.modalTitle}>
            Support creator with a tip
          </Typography>
          <IconButton
            onClick={handleClose}
            className={classes.closeButton}
            size="small"
          >
            <X size={24} />
          </IconButton>
        </Box>
        {/* {tipStep === 0 && (
          <Box style={{ padding: "16px 24px 0" }}>
            <ProgressSteps totalSteps={2} currentStep={tipStep} />
          </Box>
        )} */}
        <DialogContent className={classes.modalContent}>
          {tipStep === 0 && (
            <>
              <Typography className={classes.selectTipLabel}>
                Select tip amount:
              </Typography>
              {suggestedTips && suggestedTips.length > 0 && (
                <Box className={classes.suggestedTipsContainer}>
                  {suggestedTips.map(amount => (
                    <Button
                      key={amount}
                      onClick={() => handleSuggestedTipClick(amount)}
                      className={`${classes.suggestedTipButton} ${selectedSuggestedAmount === amount ? "selected" : ""
                        }`}
                    >
                      ${amount}
                    </Button>
                  ))}
                </Box>
              )}
              <Box className={classes.selectTipDivider}>
                <Typography className={classes.selectTipDividerText}>
                  or
                </Typography>
              </Box>
              <Box className={classes.enterAmountContainer}>
                <Box className={classes.enterAmountHeader}>
                  <Typography className={classes.enterAmountLabel}>
                    Enter your amount:
                  </Typography>
                  {minimumTipAmount && (
                    <Typography className={classes.minimumTipText}>
                      Minimum tip amount is ${minimumTipAmount}
                    </Typography>
                  )}
                </Box>
                <Box className={classes.tipInputContainer}>
                  <TextField
                    variant="outlined"
                    type="number"
                    fullWidth
                    value={tipAmount || ""}
                    onChange={e => {
                      const value =
                        e.target.value === "" ? 0 : Number(e.target.value);
                      setTipAmount(Math.max(0, value));
                      setSelectedSuggestedAmount(null);
                    }}
                    inputProps={{ min: minimumTipAmount || 0 }}
                    placeholder="0"
                    className={classes.tipInput}
                  />
                  <IconButton
                    className={classes.decrementButton}
                    onClick={handleDecrement}
                    disabled={!tipAmount || tipAmount === 0}
                  >
                    <Minus />
                  </IconButton>
                  <IconButton
                    className={classes.incrementButton}
                    onClick={handleIncrement}
                  >
                    <Plus />
                  </IconButton>
                </Box>
                <Typography className={classes.tipInfoText}>
                  You will be redirected to CCBill's secure payment portal to
                  complete your transaction.
                </Typography>
              </Box>
            </>
          )}
          {tipStep === 1 && tipAmount > 0 && (
            <>
              <Box style={{ padding: "16px 24px 0" }}>
                <ProgressSteps totalSteps={2} currentStep={tipStep} />
              </Box>
              <CCBillPayment
                price={tipAmount}
                context="tip"
                title="Complete Your Tip"
                buttonText={`Send $${tipAmount} Tip`}
                description={`Send a $${tipAmount} tip to ${username || creatorName || "this creator"
                  }`}
                onProceed={handleTip}
                onPaymentSuccess={handlePaymentSuccess}
                userId={userId}
                email={email}
                tipForUserId={tipForUserId}
                conversationId={conversationId}
              />
            </>
          )}
        </DialogContent>
        {tipStep === 0 && (
          <DialogActions className={classes.modalActions}>
            <Button onClick={handleClose} className={classes.cancelButton}>
              Cancel
            </Button>
            <Button onClick={handleTip} className={classes.sendButton}>
              Proceed
            </Button>
          </DialogActions>
        )}
      </Box>
    </Modal>
  );
};

export default TipDialog;
